import React from 'react';
import { TextField, FormControl, InputLabel, Select, MenuItem, Grid, Typography, FormControlLabel, RadioGroup, Radio, FormLabel } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { authStyles } from '../common/AuthStyles';

const UserForm = ({ 
  formData,
  handleChange,
  isDisabled = false,
  showPassword = false,
  showEmail = true,
  nameReadOnly = false,
  showDietPreference = true,
}) => {
  const { 
    email,
    firstName, 
    lastName, 
    dateOfBirth, 
    gender,
    password,
    confirmPassword,
    dietPreference,
    lifestage 
  } = formData;

  // Helper function to check if a date is valid (18 years or older)
  const isDateValid = (date) => {
    if (!date) return false;
    const eighteenYearsAgo = dayjs().subtract(18, 'year');
    return date.isBefore(eighteenYearsAgo, 'month');
  };

  // Helper function to determine if user should see lifestage questions
  const shouldShowLifestageQuestions = () => {
    if (gender !== 'female' || !dateOfBirth) return false;
    
    const birthDate = dayjs(dateOfBirth);
    const age = dayjs().diff(birthDate, 'year');
    return age >= 18 && age <= 50;
  };

  return (
    <Grid container spacing={2}>
      {/* Email - Full width - only show if showEmail is true */}
      {showEmail && (
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            value={email}
            onChange={handleChange}
            disabled={isDisabled}
            InputProps={showPassword ? {} : {
              readOnly: true,
            }}
            helperText={showPassword ? undefined : "To change your email address, use the Account & Profile section"}
            sx={{
              ...authStyles.textField,
              ...(showPassword ? {} : {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(158, 179, 132, 0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(158, 179, 132, 0.08)',
                  },
                }
              })
            }}
          />
        </Grid>
      )}

      {/* Name fields - Side by side */}
      <Grid item xs={12} sm={6}>
        <TextField
          variant="outlined"
          required
          fullWidth
          id="firstName"
          label="First Name"
          name="firstName"
          value={firstName}
          onChange={handleChange}
          disabled={isDisabled}
          InputProps={nameReadOnly ? {
            readOnly: true,
          } : {}}
          helperText={nameReadOnly ? "From your Google account" : undefined}
          sx={{
            ...authStyles.textField,
            ...(nameReadOnly ? {
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(158, 179, 132, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(158, 179, 132, 0.08)',
                },
              }
            } : {})
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          variant="outlined"
          required={!nameReadOnly}
          fullWidth
          id="lastName"
          label="Last Name"
          name="lastName"
          value={lastName}
          onChange={handleChange}
          disabled={isDisabled}
          InputProps={nameReadOnly ? {
            readOnly: true,
          } : {}}
          helperText={nameReadOnly ? (lastName ? "From your Google account" : "Not provided by Google") : undefined}
          sx={{
            ...authStyles.textField,
            ...(nameReadOnly ? {
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(158, 179, 132, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(158, 179, 132, 0.08)',
                },
              }
            } : {})
          }}
        />
      </Grid>

      {/* Date of Birth and Gender section */}

      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          This information helps tailor recommendations for your unique profile
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Birth Month & Year"
            views={['year', 'month']}
            format="MMMM YYYY"
            value={dateOfBirth ? dayjs(dateOfBirth) : null}
            onChange={(newValue) => {
              handleChange({
                target: {
                  name: 'dateOfBirth',
                  value: newValue ? newValue.format('YYYY-MM') : ''
                }
              });
            }}
            maxDate={dayjs().subtract(18, 'year').subtract(1, 'month').endOf('month')}
            minDate={dayjs('1900-01')}
            defaultView="year"
            sx={{ ...authStyles.textField, width: '100%' }}
            slotProps={{
              textField: {
                helperText: 'Must be 18 or older',
                placeholder: 'MM/YYYY',
                InputProps: {
                  readOnly: true,
                  placeholder: 'MM/YYYY'
                }
              },
              actionBar: {
                actions: ['accept']
              },
              field: {
                clearable: false
              }
            }}
          />
        </LocalizationProvider>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="gender-label">Gender</InputLabel>
          <Select
            labelId="gender-label"
            id="gender"
            name="gender"
            value={gender}
            onChange={handleChange}
            label="Gender"
          >
            <MenuItem value="">
              <em>Prefer not to say</em>
            </MenuItem>
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Diet Preference section (optional) */}
      {showDietPreference && (
        <>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Sharing your diet helps spot common nutrient gaps
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="diet-preference-label">Diet Preference</InputLabel>
              <Select
                labelId="diet-preference-label"
                id="dietPreference"
                name="dietPreference"
                value={dietPreference || ''}
                onChange={handleChange}
                label="Diet Preference"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Vegan">Vegan - No meat, fish, eggs, or dairy</MenuItem>
                <MenuItem value="Vegetarian">Vegetarian - No meat or fish, but eggs and dairy are A-OK!</MenuItem>
                <MenuItem value="Keto">Keto - High-fat and super low-carb</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </>
      )}

      {/* Lifestage section - conditionally shown for females 18-50 */}
      {shouldShowLifestageQuestions() && (
        <>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Lifestage information helps us calculate accurate nutrient recommendations
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Are you currently pregnant?</FormLabel>
              <RadioGroup
                row
                name="pregnant"
                value={lifestage?.pregnant !== undefined ? String(lifestage.pregnant) : ''}
                onChange={(e) => {
                  handleChange({
                    target: {
                      name: 'lifestage',
                      value: {
                        ...(lifestage || {}),
                        pregnant: e.target.value === 'true'
                      }
                    }
                  });
                }}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Are you currently breastfeeding?</FormLabel>
              <RadioGroup
                row
                name="breastfeeding"
                value={lifestage?.breastfeeding !== undefined ? String(lifestage.breastfeeding) : ''}
                onChange={(e) => {
                  handleChange({
                    target: {
                      name: 'lifestage',
                      value: {
                        ...(lifestage || {}),
                        breastfeeding: e.target.value === 'true'
                      }
                    }
                  });
                }}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Grid>
        </>
      )}

      {/* Password fields - Full width */}
      {showPassword && (
        <>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={handleChange}
              sx={authStyles.textField}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={handleChange}
              sx={authStyles.textField}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default UserForm;
